import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { trackEvent } from '@/lib/analytics';

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2200, 3000, 4000, 5500, 7500, 10000];

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first-quiz', name: 'Premier Quiz', description: 'Complétez votre premier quiz', icon: '🎯' },
  { id: 'streak-3', name: 'En série !', description: '3 quiz consécutifs', icon: '🔥' },
  { id: 'streak-5', name: 'Inarrêtable !', description: '5 quiz consécutifs', icon: '💪' },
  { id: 'perfect', name: 'Perfection', description: 'Score de 100% sur un quiz', icon: '⭐' },
  { id: 'night-owl', name: 'Oiseau de nuit', description: 'Quiz après 22h', icon: '🦉' },
  { id: 'early-bird', name: 'Lève-tôt', description: 'Quiz avant 7h', icon: '🌅' },
  { id: 'level-5', name: 'Niveau 5', description: 'Atteindre le niveau 5', icon: '🏆' },
  { id: 'xp-1000', name: '1000 XP', description: 'Accumuler 1000 points d\'expérience', icon: '💎' },
  { id: 'ten-quizzes', name: 'Habitué', description: 'Compléter 10 quiz', icon: '📚' },
  { id: 'explorer', name: 'Explorateur', description: 'Essayer 3 examens différents', icon: '🧭' },
];

interface GamificationState {
  totalXP: number;
  level: number;
  streak: number;
  lastQuizDate: string | null;
  quizCount: number;
  examsExplored: string[];
  achievements: string[];
  newAchievements: string[];
}

interface GamificationActions {
  addXP: (points: number) => void;
  incrementStreak: () => void;
  resetStreak: () => void;
  recordQuiz: (examId: string, score: number) => void;
  unlockAchievement: (id: string) => void;
  clearNewAchievements: () => void;
  getXPProgress: () => { current: number; needed: number; percent: number };
  syncFromServer: (data: { totalXp: number; level: number; currentStreak: number }) => void;
  /** Remise à zéro complète (déconnexion : purge des données locales). */
  resetAll: () => void;
}

function getLevel(xp: number): number {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 1;
}

function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

export const useGamificationStore = create<GamificationState & GamificationActions>()(
  persist(
    (set, get) => ({
      totalXP: 0,
      level: 1,
      streak: 0,
      lastQuizDate: null,
      quizCount: 0,
      examsExplored: [],
      achievements: [],
      newAchievements: [],

      resetAll: () =>
        set({
          totalXP: 0,
          level: 1,
          streak: 0,
          lastQuizDate: null,
          quizCount: 0,
          examsExplored: [],
          achievements: [],
          newAchievements: [],
        }),

      addXP: (points) => {
        const prevLevel = get().level;
        const newXP = get().totalXP + points;
        const newLevel = getLevel(newXP);
        set({ totalXP: newXP, level: newLevel });
        if (newLevel > prevLevel) trackEvent('level_up', { level: newLevel });
        if (newXP >= 1000) get().unlockAchievement('xp-1000');
        if (newLevel >= 5) get().unlockAchievement('level-5');
      },

      incrementStreak: () => {
        const today = getToday();
        const { lastQuizDate, streak } = get();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        let newStreak: number;
        if (lastQuizDate === today) {
          newStreak = streak;
        } else if (lastQuizDate === yesterdayStr) {
          newStreak = streak + 1;
        } else {
          newStreak = 1;
        }

        set({ streak: newStreak, lastQuizDate: today });
        if (newStreak >= 3) get().unlockAchievement('streak-3');
        if (newStreak >= 5) get().unlockAchievement('streak-5');
      },

      resetStreak: () => set({ streak: 0 }),

      recordQuiz: (examId, score) => {
        const state = get();
        const newCount = state.quizCount + 1;
        const newExams = state.examsExplored.includes(examId)
          ? state.examsExplored
          : [...state.examsExplored, examId];

        set({ quizCount: newCount, examsExplored: newExams });

        if (newCount === 1) get().unlockAchievement('first-quiz');
        if (newCount >= 10) get().unlockAchievement('ten-quizzes');
        if (score === 100) get().unlockAchievement('perfect');
        if (newExams.length >= 3) get().unlockAchievement('explorer');

        const hour = new Date().getHours();
        if (hour >= 22 || hour < 5) get().unlockAchievement('night-owl');
        if (hour >= 5 && hour < 7) get().unlockAchievement('early-bird');
      },

      unlockAchievement: (id) => {
        if (get().achievements.includes(id)) return;
        set((s) => ({
          achievements: [...s.achievements, id],
          newAchievements: [...s.newAchievements, id],
        }));
      },

      clearNewAchievements: () => set({ newAchievements: [] }),

      getXPProgress: () => {
        const { totalXP, level } = get();
        const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
        const nextThreshold = LEVEL_THRESHOLDS[level] || currentThreshold + 500;
        const current = totalXP - currentThreshold;
        const needed = nextThreshold - currentThreshold;
        return { current, needed, percent: Math.min(100, (current / needed) * 100) };
      },

      syncFromServer: ({ totalXp, level, currentStreak }) => {
        set({
          totalXP: totalXp,
          level,
          streak: currentStreak,
        });
      },
    }),
    { name: 'certexam-gamification' }
  )
);
